<template>
  <div class="min-height-100">
    <template v-if="isMobile">
      <mobile-filter-header :show-filters="() => setMobileFilterVisibility(true)" :sorting-options="sortingOptions" />
      <mobile-filter-panel
        :total-count="pagination.totalCount"
        :show="showMobileFilter"
        :hide-filters="() => setMobileFilterVisibility(false)"
        :filter-panel-config="getFilterPanelConfig()"
      />
    </template>
    <div class="search-result-page">
      <filter-panel
        v-if="!isMobile"
        :total-count="pagination.totalCount"
        :filter-panel-config="getFilterPanelConfig()"
      ></filter-panel>
      <div class="page-content">
        <div class="page-header">
          <div v-if="collectionInfo && collectionInfo.parentCategoryId" class="back-to-parent" @click="backToParent()">
            <inline-svg class="clear-icon" :src="require('@/assets/img/icons/chevron_left.svg')"></inline-svg>
            <h4 class="parent-category">
              {{ collectionInfo.parentCategoryName }}
            </h4>
          </div>
          <div class="page-title-row">
            <div class="page-title">{{ getPageTitle() }}</div>
            <sorting-options v-if="!isMobile" :options="sortingOptions" />
          </div>
          <h4 v-if="isMobile && pagination.totalCount" class="mobile-total-count color-gray">
            {{ pagination.totalCount }} TOTAL RESULTS
          </h4>
          <filter-badges class="filter-badges" />
        </div>
        <product-tiles :loading="loading" :products="products" :pagination="pagination" :set-page="setPage" />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped src="../../assets/sass/searchPage/searchPage.scss"></style>

<script>
import { searchPageMobileStateHelper } from "../../components/mixin/search/searchPageMobileStateHelper";
import { FilterKeys, RouterPath } from "../../services/utils";
import InlineSvg from "vue-inline-svg";
import NavigationApi from "@/services/api/NavigationApi";
import CollectionApi from "@/services/api/collectionApi";
import { algoliaProductHelper } from "../../components/mixin/search/algoliaProductHelper";

const CollectionApiService = new CollectionApi(),
  NavigationApiService = new NavigationApi();

export default {
  components: {
    InlineSvg,
  },
  mixins: [algoliaProductHelper, searchPageMobileStateHelper],
  props: ["fromNavBar", "isParentCollection"],
  data() {
    return {
      filterOrSortUsed: false,
      filtersOrSortsUsed: new Set(),
      products: [],
      collectionInfo: null,
      navigationItems: [],
      sortingOptions: this.$utils.constants.DEFAULT_PRODUCT_SORTING_OPTIONS.slice(1, 5),
    };
  },
  metaInfo () {
    return {
      title: this.title,
      link: [
        {
          rel: 'canonical',
          href: `${window.location.origin}/${this.$route.path}`
        }
      ]
    }
  },
  watch: {
    "$route.query": function (query) {
      this.initCollectionInfo();
      this.getProducts(query);
    },
  },
  async beforeMount() {
    await Promise.all([
      this.getNavigationCategories(),
      this.getCategories(),
      this.getFavoriteProducts(),
      this.fetchCollectionIds(),
    ]);
    this.initCollectionInfo();

    if (!this.$route.query.page) {
      this.updateURLParams("page", 1);
    } else {
      this.getProducts(this.$route.query);
    }
  },
  methods: {
    getCategories,
    initCollectionInfo,
    getPageTitle,
    getFilterPanelConfig,
    findParentCategory,
    findSubCategory,
    backToParent,
    getNavigationCategories,
    getProductsSortByCollectionOrder,
    paginate,
    getProducts,
    fetchCollectionIds,
  },
};

async function getCategories() {
  this.categories = await this.$utils.getProductCategories(true);
}

function getFilterPanelConfig() {
  const { parentCategoryId } = this.collectionInfo || {};

  return {
    filterSettings: {
      [FilterKeys.CATEGORIES]: {
        parentCategoryId: parentCategoryId,
        categories: this.categories,
      },
    },
  };
}

function initCollectionInfo() {
  const parentCategory = this.findParentCategory();
  this.collectionInfo = {
    parentCategoryId: parentCategory?.id,
    parentCategoryName: parentCategory?.name,
    collectionName: this.$route.params?.collectionName,
  };
}

async function getNavigationCategories() {
  const response = (await NavigationApiService.getNavigation()).data[0];
  this.navigationItems = response.children_containers;
}

function findParentCategory() {
  const { categoryName, collectionId } = this.$route.params;

  if (!categoryName) {
    return null;
  }

  let navigationItemId = null;
  this.navigationItems.forEach((parent) => {
    parent?.children_containers?.forEach((children) => {
      children.view_components.forEach((item) => {
        if (item.id === collectionId) {
          navigationItemId = parent.id;
        }
      });
    });
  });

  return this.categories.find((item) => item.parent_container_id === navigationItemId);
}

function findSubCategory(id) {
  let subCategory = null;
  this.categories.find((parent) => {
    const found = parent.view_components?.find((item) => item.entity_id === id);
    if (found) {
      subCategory = found;
    }
  });
  return subCategory;
}

function backToParent() {
  const category = this.findParentCategory();
  this.$router.push(`/${category.name}/${category.id}/${RouterPath.CATEGORY_PRODUCTS_PAGE}`);
}

function getPageTitle() {
  return this.collectionInfo?.collectionName;
}

function getProducts(params) {
  const acceptedParameters = [...Object.keys(FilterKeys).map((item) => FilterKeys[item].toString()), ...["sortBy"]];
  const hasActiveFilter = Object.keys(params).filter(
    (param) => acceptedParameters.includes(param) && params[param]?.length
  )?.length;

  if (hasActiveFilter) {
    this.getProductsOnSearch(params);
  } else {
    this.getProductsSortByCollectionOrder(params.page);
  }
  this.setPage(params.page);
}

async function fetchCollectionIds() {
  const fetchIdsByView = this.isParentCollection
      ? NavigationApiService.getProductsForContainer
      : NavigationApiService.getProductsForComponent,
    getAllCollectionProductIds = this.fromNavBar ? fetchIdsByView : CollectionApiService.getAllProductsByCollectionId;
  try {
    const { data } = await getAllCollectionProductIds(this.$route.params.collectionId);
    const { product_ids } = data;
    this.product_ids = product_ids;
  } catch (err) {
    this.$logger.notify(new Error(err));
  }
}

function getProductsSortByCollectionOrder(page) {
  if (!this.product_ids?.length) {
    return;
  }

  this.loading = true;
  this.pagination.totalCount = this.product_ids?.length;
  const ids = this.paginate(this.product_ids, this.pagination.limit, page);
  this.algoliaClient
    .initIndex(this.$env.getOrigin() + "_products")
    .getObjects(ids)
    .then(({ results }) => {
      this.products = this.applyFavorites(results);
    })
    .finally(() => {
      this.loading = false;
    });
}

function paginate(array, page_size, page_number) {
  return array.slice((page_number - 1) * page_size, page_number * page_size);
}
</script>
